import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { YoutubePreview } from '@/components/content/youtube-preview';
import { ChurchLogoUploader } from '@/components/shared/church-logo-uploader';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PermissionGate } from '@/components/shared/permission-gate';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS } from '@/lib/permissions';
import { uploadBlob } from '@/lib/upload';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { formatDateTime } from '@/utils/format';

interface Video {
  id: string;
  title: string;
  description: string | null;
  youtubeUrl: string;
  thumbnailKey: string | null;
  thumbnailUrl: string | null;
  kind: 'video' | 'live';
  status: 'draft' | 'scheduled' | 'published';
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  canDelete?: boolean;
}

const publishDisplay = (row: Video): string | null => row.publishAt ?? row.publishedAt;

const videoStatusVariant = (status: Video['status']) => {
  if (status === 'published') return 'success' as const;
  if (status === 'scheduled') return 'info' as const;
  return 'neutral' as const;
};

const saveToastKey = (
  status: Video['status'],
  wasPublished: boolean,
): 'videos.published' | 'videos.scheduled' | 'videos.saved' => {
  if (status === 'published' && !wasPublished) return 'videos.published';
  if (status === 'scheduled') return 'videos.scheduled';
  return 'videos.saved';
};

type PublishTiming = 'draft' | 'now' | 'schedule';

const emptyForm = () => ({
  title: '',
  description: '',
  youtubeUrl: '',
  kind: 'video' as 'video' | 'live',
  thumbnailKey: null as string | null,
  thumbnailPreviewUrl: null as string | null,
  publishTiming: 'draft' as PublishTiming,
  publishAt: null as string | null,
});

export const VideosPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Video | null>(null);
  const [detail, setDetail] = useState<Video | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [publishTarget, setPublishTarget] = useState<Video | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<Video | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Video | null>(null);

  const dateRange = useContentPeriodStore((state) => state.dateRange);

  const list = useQuery({
    queryKey: ['content', 'videos', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<{ data: Video[] }>('/content/videos', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (video: Video) => {
    setEditing(video);
    setForm({
      title: video.title,
      description: video.description ?? '',
      youtubeUrl: video.youtubeUrl,
      kind: video.kind,
      thumbnailKey: video.thumbnailKey,
      thumbnailPreviewUrl: video.thumbnailUrl,
      publishTiming: video.status === 'scheduled' ? 'schedule' : 'draft',
      publishAt: video.publishAt,
    });
    setDetail(null);
    setFormOpen(true);
  };

  const editingPublished = editing?.status === 'published';

  const scheduleInPast =
    form.publishTiming === 'schedule' &&
    form.publishAt !== null &&
    new Date(form.publishAt).getTime() <= Date.now();

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() ? form.description.trim() : null,
        youtubeUrl: form.youtubeUrl.trim(),
        kind: form.kind,
        thumbnailKey: form.thumbnailKey,
        ...(editingPublished
          ? {}
          : { publishAt: form.publishTiming === 'schedule' ? form.publishAt : null }),
      };

      if (editing) {
        const updated = await api.patch<Video>(`/content/videos/${editing.id}`, body);
        if (!editingPublished && form.publishTiming === 'now' && updated.status !== 'published') {
          return api.post<Video>(`/content/videos/${updated.id}/publish`);
        }
        return updated;
      }

      const created = await api.post<Video>('/content/videos', body);
      if (form.publishTiming === 'now') {
        return api.post<Video>(`/content/videos/${created.id}/publish`);
      }
      return created;
    },
    onSuccess: (video) => {
      toast.success(t(saveToastKey(video.status, editing?.status === 'published')));
      setFormOpen(false);
      resetForm();
      setDetail(video);
      void queryClient.invalidateQueries({ queryKey: ['content', 'videos'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Video>(`/content/videos/${id}/publish`),
    onSuccess: (video) => {
      toast.success(t('videos.published'));
      setPublishTarget(null);
      setDetail((current) => (current?.id === video.id ? video : current));
      void queryClient.invalidateQueries({ queryKey: ['content', 'videos'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Video>(`/content/videos/${id}/unpublish`),
    onSuccess: (video) => {
      toast.success(t('videos.unpublished'));
      setUnpublishTarget(null);
      setDetail((current) => (current?.id === video.id ? video : current));
      void queryClient.invalidateQueries({ queryKey: ['content', 'videos'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/videos/${id}`),
    onSuccess: () => {
      toast.success(t('videos.deleted'));
      setDeleteTarget(null);
      setDetail(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'videos'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Video>[] = [
    {
      id: 'createdAt',
      header: t('videos.columns.createdAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'publishAt',
      header: t('videos.columns.publishAt'),
      cell: (row) => {
        const value = publishDisplay(row);
        return value ? formatDateTime(value) : '—';
      },
    },
    {
      id: 'title',
      header: t('videos.columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'kind',
      header: t('videos.columns.kind'),
      cell: (row) => (row.kind === 'live' ? t('videos.kindLive') : t('videos.kindVideo')),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('videos.columns.status'),
      cell: (row) => (
        <Badge variant={videoStatusVariant(row.status)}>{t(`status.${row.status}`)}</Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const canSave =
    form.title.trim().length > 0 &&
    form.youtubeUrl.trim().length > 0 &&
    (editingPublished ||
      (!scheduleInPast &&
        (form.publishTiming === 'draft' ||
          form.publishTiming === 'now' ||
          form.publishAt !== null)));

  const detailShowPublish = detail?.status === 'draft' || detail?.status === 'scheduled';

  return (
    <div>
      <ContentPageHeader
        actions={
          <PermissionGate permission={PERMISSIONS.CONTENT_CREATE}>
            <Button onClick={openCreate}>{t('videos.create')}</Button>
          </PermissionGate>
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('videos.empty')}</p>}
        onRowClick={(row) => setDetail(row)}
        rowActions={(row) => (
          <div className="flex justify-end gap-2">
            {row.status === 'published' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setUnpublishTarget(row)}
              >
                {t('videos.unpublish')}
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPublishTarget(row)}>
                {t('videos.publish')}
              </Button>
            )}
            {row.canDelete === true ? (
              <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                <Button size="sm" variant="outline" onClick={() => setDeleteTarget(row)}>
                  {tCommon('action.delete')}
                </Button>
              </PermissionGate>
            ) : null}
          </div>
        )}
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{detail?.title ?? t('videos.title')}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <>
              <DialogBody contentClassName="flex flex-col gap-6">
                <YoutubePreview
                  url={detail.youtubeUrl}
                  title={t('videos.previewTitle', { title: detail.title })}
                  unavailableLabel={t('videos.previewUnavailable')}
                  watchLabel={t('videos.watchOnYoutube')}
                />
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={videoStatusVariant(detail.status)}>
                      {t(`status.${detail.status}`)}
                    </Badge>
                    <span className="text-body-sm text-fg-secondary">
                      {detail.kind === 'live' ? t('videos.kindLive') : t('videos.kindVideo')}
                    </span>
                  </div>
                  <dl className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('videos.columns.createdAt')}
                      </dt>
                      <dd className="text-body-sm">{formatDateTime(detail.createdAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('videos.columns.publishAt')}
                      </dt>
                      <dd className="text-body-sm">
                        {publishDisplay(detail) ? formatDateTime(publishDisplay(detail)!) : '—'}
                      </dd>
                    </div>
                  </dl>
                  {detail.description ? (
                    <div>
                      <p className="text-caption text-fg-secondary">{t('videos.descriptionLabel')}</p>
                      <p className="mt-1 whitespace-pre-wrap text-body-sm">{detail.description}</p>
                    </div>
                  ) : null}
                </div>
              </DialogBody>
              <DialogFooter>
                {detail.status === 'published' ? (
                  <Button variant="outline" onClick={() => setUnpublishTarget(detail)}>
                    {t('videos.unpublish')}
                  </Button>
                ) : null}
                {detailShowPublish ? (
                  <Button variant="outline" onClick={() => setPublishTarget(detail)}>
                    {t('videos.publish')}
                  </Button>
                ) : null}
                {detail.canDelete === true ? (
                  <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                    <Button variant="outline" onClick={() => setDeleteTarget(detail)}>
                      {tCommon('action.delete')}
                    </Button>
                  </PermissionGate>
                ) : null}
                <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                  <Button onClick={() => openEdit(detail)}>{tCommon('action.edit')}</Button>
                </PermissionGate>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t('confirm.publishVideo.title', { name: publishTarget?.title ?? '' })}
        description={t('confirm.publishVideo.description')}
        confirmLabel={t('confirm.publishVideo.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnpublishTarget(null);
        }}
        title={t('confirm.unpublishVideo.title', { name: unpublishTarget?.title ?? '' })}
        description={t('confirm.unpublishVideo.description')}
        confirmLabel={t('confirm.unpublishVideo.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('confirm.deleteVideo.title', { name: deleteTarget?.title ?? '' })}
        description={t('confirm.deleteVideo.description')}
        confirmLabel={t('confirm.deleteVideo.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog
        open={formOpen}
        onOpenChange={(next) => {
          setFormOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{editing ? t('videos.edit') : t('videos.create')}</DialogTitle>
          </DialogHeader>

          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div>
                <Label htmlFor="video-title">{t('videos.titleLabel')}</Label>
                <Input
                  id="video-title"
                  value={form.title}
                  onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="video-description">{t('videos.descriptionLabel')}</Label>
                <Textarea
                  id="video-description"
                  value={form.description}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, description: e.target.value }))
                  }
                  placeholder={t('videos.descriptionPlaceholder')}
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="video-url">{t('videos.urlLabel')}</Label>
                <Input
                  id="video-url"
                  type="url"
                  value={form.youtubeUrl}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, youtubeUrl: e.target.value }))
                  }
                  placeholder={
                    form.kind === 'live'
                      ? t('videos.liveUrlPlaceholder')
                      : t('videos.urlPlaceholder')
                  }
                />
              </div>

              <div>
                <Label>{t('videos.kindLabel')}</Label>
                <Listbox
                  value={form.kind}
                  onChange={(value) => {
                    const next = (value as 'video' | 'live') ?? 'video';
                    setForm((current) => ({ ...current, kind: next }));
                  }}
                  options={[
                    { value: 'video', label: t('videos.kindVideo') },
                    { value: 'live', label: t('videos.kindLive') },
                  ]}
                  popoverPortal="dialog"
                />
                <Tip
                  tipId={form.kind === 'live' ? 'content.video.live' : 'content.video.when'}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>{t('videos.thumbnailLabel')}</Label>
              <Tip tipId="content.video.thumbnail" className="mb-2" />
              <ChurchLogoUploader
                layout="inline"
                src={form.thumbnailPreviewUrl}
                name={form.title || t('videos.title')}
                onUpload={async (blob) => {
                  const { key } = await uploadBlob({ purpose: 'content_artwork', blob });
                  setForm((current) => ({
                    ...current,
                    thumbnailKey: key,
                    thumbnailPreviewUrl: URL.createObjectURL(blob),
                  }));
                }}
                onRemove={
                  form.thumbnailKey
                    ? () =>
                        setForm((current) => ({
                          ...current,
                          thumbnailKey: null,
                          thumbnailPreviewUrl: null,
                        }))
                    : undefined
                }
                labels={{
                  change: t('videos.thumbnailChange'),
                  remove: t('videos.thumbnailRemove'),
                  cropTitle: t('videos.thumbnailCropTitle'),
                  zoom: tCommon('media.zoom'),
                  apply: t('videos.thumbnailApply'),
                  cancel: tCommon('action.cancel'),
                  instructions: t('videos.thumbnailInstructions'),
                  closeCropper: tCommon('action.close'),
                }}
              />
            </div>

            {editingPublished ? null : (
              <div className="flex flex-col gap-4">
                <div>
                  <Label>{t('videos.whenLabel')}</Label>
                  <Listbox
                    value={form.publishTiming}
                    onChange={(value) => {
                      const next = (value as PublishTiming) ?? 'draft';
                      setForm((current) => ({
                        ...current,
                        publishTiming: next,
                        publishAt: next === 'schedule' ? current.publishAt : null,
                      }));
                    }}
                    options={[
                      { value: 'draft', label: t('videos.whenDraft') },
                      { value: 'now', label: t('videos.whenNow') },
                      { value: 'schedule', label: t('videos.whenSchedule') },
                    ]}
                    popoverPortal="dialog"
                  />
                  <Tip tipId="content.video.when" className="mt-1" />
                </div>

                {form.publishTiming === 'schedule' ? (
                  <div>
                    <Label>{t('videos.publishAtLabel')}</Label>
                    <DateTimePicker
                      value={form.publishAt}
                      onChange={(publishAt) => setForm((current) => ({ ...current, publishAt }))}
                      invalid={scheduleInPast}
                      popoverPortal="dialog"
                      timezoneNotice={(timezone) => t('videos.timezoneNotice', { timezone })}
                    />
                    {scheduleInPast ? (
                      <p className="mt-1 text-caption text-danger">{t('videos.publishAtFuture')}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              disabled={!canSave || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {!editing && form.publishTiming === 'now' ? t('videos.publish') : tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
